import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// 1. Ant Design
import { Button, Flex, List, Modal, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
// 2. External libraries
import { observer } from "mobx-react-lite";
import { useState } from "react";
// 3. Internal components
import CompanyLinkAddForm from "./CompanyLinkAddForm";
import CompanyLinkBlockModal from "./CompanyLinkBlockModal";
import CompanyLinkEditModal from "./CompanyLinkEditModal";
// 5. Types, utils, styles
import { BLOCKED_BY_KIND_LABELS, CORP_LINK_STATE_COLORS, CORP_LINK_STATE_LABELS, CORP_COMPANY_STATUS_COLORS, LINK_ENDED_REASON_LABELS, } from "@/types/company/company.common";
import { CORP_COMPANY_STATUS_LABELS } from "@/types/corp/corpSession.common";
import { formatLinkDateTime, formatPhoneE164 } from "./linkFormat";
import styles from "@/styles/CompanyLinks.module.less";
const { Text } = Typography;
/**
 * Привязки сотрудников — общий компонент персонала (UC1115, UC1117) и кабинета (WP-DSP-3).
 * Данные и действия — только через стор с `ILinksApi`. Состояние и доступность действий решаются по полям
 * `state` и `isBlocked` из данных; сравнений сроков с текущим временем здесь нет (CCR-009).
 */
const CompanyLinks = observer(({ store, companyOptions, clientPhone, emptyText }) => {
    const [editLink, setEditLink] = useState(null);
    const [blockLink, setBlockLink] = useState(null);
    // Читаем наблюдаемые поля в теле observer-компонента: рендер-функции колонок antd вызываются
    // внутри rc-table, и чтение store.* там не попадает в реакцию этого компонента (значения «залипают»).
    const { isSubmitting, readOnly, isLoading, links, stateFilter, scope, canUnblockStaff } = store;
    const isClientScope = scope === "client";
    const showOpenOrders = () => {
        if (store.lastOpenOrders.length === 0)
            return;
        Modal.info({
            title: "Незавершённые заказы сотрудника",
            content: (_jsxs(_Fragment, { children: [_jsx(Text, { children: "\u042D\u0442\u0438 \u0437\u0430\u043A\u0430\u0437\u044B \u043E\u0441\u0442\u0430\u043D\u0443\u0442\u0441\u044F \u043A\u043E\u0440\u043F\u043E\u0440\u0430\u0442\u0438\u0432\u043D\u044B\u043C\u0438 \u0438 \u0431\u0443\u0434\u0443\u0442 \u0432\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u044B:" }), _jsx(List, { size: "small", dataSource: store.lastOpenOrders, renderItem: (order) => (_jsxs(List.Item, { children: [_jsxs(Text, { strong: true, children: ["\u2116 ", order.orderNumber ?? order.orderId.slice(0, 8)] }), _jsx(Text, { type: "secondary", children: formatLinkDateTime(order.scheduledDate) })] })) })] })),
            onOk: () => store.clearOpenOrders(),
        });
    };
    const confirmEnd = (link) => {
        Modal.confirm({
            title: "Отвязать сотрудника?",
            content: "Привязка сразу завершится, новые корпоративные заказы сотрудника станут невозможны. Уже созданные заказы останутся корпоративными и будут выполнены.",
            okText: "Отвязать",
            okButtonProps: { danger: true },
            cancelText: "Отмена",
            onOk: async () => {
                const ended = await store.end(link.linkId);
                if (ended)
                    showOpenOrders();
            },
        });
    };
    const confirmUnblock = (link) => {
        Modal.confirm({
            title: "Разблокировать привязку?",
            content: link.blockedByKind === "CORP_ADMIN"
                ? "Блокировку установила компания. Сотрудник снова сможет оформлять заказы за счёт компании."
                : "Блокировку установил оператор. Сотрудник снова сможет оформлять заказы за счёт компании.",
            okText: "Разблокировать",
            cancelText: "Отмена",
            onOk: () => store.setBlocked(link.linkId, false, null),
        });
    };
    const renderActions = (link) => {
        if (readOnly || link.state === "EXPIRED")
            return _jsx(Text, { type: "secondary", children: "\u2014" });
        return (_jsxs(Space, { size: 4, wrap: true, children: [_jsx(Button, { size: "small", onClick: () => setEditLink(link), disabled: isSubmitting, children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C" }), link.isBlocked ? (!canUnblockStaff && link.blockedByKind === "STAFF" ? (_jsx(Text, { type: "secondary", children: "\u043E\u043F\u0435\u0440\u0430\u0442\u043E\u0440" })) : (_jsx(Button, { size: "small", onClick: () => confirmUnblock(link), disabled: isSubmitting, "data-testid": `corp-links-row-${link.linkId}-block`, children: "\u0420\u0430\u0437\u0431\u043B\u043E\u043A\u0438\u0440\u043E\u0432\u0430\u0442\u044C" }))) : (_jsx(Button, { size: "small", danger: true, onClick: () => setBlockLink(link), disabled: isSubmitting, "data-testid": `corp-links-row-${link.linkId}-block`, children: "\u0417\u0430\u0431\u043B\u043E\u043A\u0438\u0440\u043E\u0432\u0430\u0442\u044C" })), _jsx(Button, { size: "small", onClick: () => confirmEnd(link), disabled: isSubmitting, "data-testid": `corp-links-row-${link.linkId}-end`, children: "\u041E\u0442\u0432\u044F\u0437\u0430\u0442\u044C" })] }));
    };
    const columns = [
        ...(isClientScope
            ? [
                {
                    title: "Компания",
                    key: "companyName",
                    render: (_, link) => (_jsxs(Space, { direction: "vertical", size: 2, children: [_jsx(Text, { strong: true, children: link.companyName }), _jsxs(Space, { size: 4, wrap: true, children: [link.companyStatus ? (_jsx(Tag, { color: CORP_COMPANY_STATUS_COLORS[link.companyStatus], children: CORP_COMPANY_STATUS_LABELS[link.companyStatus] })) : null, link.canOrder === null ? null : (_jsx(Tag, { color: link.canOrder ? "green" : "default", children: link.canOrder ? "Может заказывать" : "Не может заказывать" }))] })] })),
                },
            ]
            : []),
        {
            title: "Подпись",
            key: "employeeLabel",
            render: (_, link) => link.employeeLabel ?? _jsx(Text, { type: "secondary", children: "\u2014" }),
        },
        {
            title: "Телефон",
            key: "phoneE164",
            render: (_, link) => (_jsx("span", { className: styles.phone, children: formatPhoneE164(link.phoneE164) })),
        },
        ...(isClientScope
            ? []
            : [
                {
                    title: "Клиент",
                    key: "clientName",
                    render: (_, link) => link.clientName ?? _jsx(Text, { type: "secondary", children: "\u2014" }),
                },
            ]),
        {
            title: "Состояние",
            key: "state",
            render: (_, link) => {
                const tag = (_jsx(Tag, { color: CORP_LINK_STATE_COLORS[link.state], "data-testid": `corp-links-row-${link.linkId}-state`, children: CORP_LINK_STATE_LABELS[link.state] }));
                return link.endedReason ? (_jsx(Tooltip, { title: `Закрыта: ${LINK_ENDED_REASON_LABELS[link.endedReason]}`, children: tag })) : (tag);
            },
        },
        {
            title: "Срок",
            key: "period",
            render: (_, link) => (_jsxs(Space, { direction: "vertical", size: 0, children: [_jsxs(Text, { children: ["\u0441 ", formatLinkDateTime(link.validFrom)] }), _jsx(Text, { type: "secondary", children: link.validTo ? `до ${formatLinkDateTime(link.validTo)}` : "бессрочно" })] })),
        },
        {
            title: "Блокировка",
            key: "block",
            render: (_, link) => link.isBlocked ? (_jsxs(Space, { direction: "vertical", size: 0, children: [_jsxs(Text, { children: [link.blockedByKind ? BLOCKED_BY_KIND_LABELS[link.blockedByKind] : "—", ", ", formatLinkDateTime(link.blockedAt)] }), link.blockReason ? _jsx(Text, { type: "secondary", children: link.blockReason }) : null] })) : (_jsx(Text, { type: "secondary", children: "\u2014" })),
        },
        {
            title: "Заказов",
            key: "ordersCount",
            width: 90,
            render: (_, link) => link.ordersCount,
        },
        {
            title: "Действия",
            key: "actions",
            render: (_, link) => renderActions(link),
        },
    ];
    const canAdd = !readOnly && (!isClientScope || Boolean(clientPhone));
    return (_jsxs("div", { className: styles.container, children: [canAdd ? (_jsx(CompanyLinkAddForm, { store: store, companyOptions: companyOptions, fixedPhone: clientPhone ?? undefined })) : null, !readOnly && isClientScope && !clientPhone ? (_jsx(Text, { type: "secondary", className: styles.hint, children: "\u0412 \u043F\u0440\u043E\u0444\u0438\u043B\u0435 \u043A\u043B\u0438\u0435\u043D\u0442\u0430 \u043D\u0435\u0442 \u0442\u0435\u043B\u0435\u0444\u043E\u043D\u0430 \u2014 \u043F\u0440\u0438\u0432\u044F\u0437\u043A\u0443 \u043F\u043E \u0442\u0435\u043B\u0435\u0444\u043E\u043D\u0443 \u0441\u043E\u0437\u0434\u0430\u0442\u044C \u043D\u0435\u043B\u044C\u0437\u044F." })) : null, _jsxs(Flex, { justify: "space-between", align: "center", gap: 12, wrap: "wrap", className: styles.toolbar, children: [_jsx(Select, { allowClear: true, placeholder: "\u0412\u0441\u0435 \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u044F", className: styles.stateFilter, value: stateFilter ?? undefined, onChange: (value) => store.setStateFilter(value ?? null), options: Object.entries(CORP_LINK_STATE_LABELS).map(([value, label]) => ({ value: value, label })) }), _jsx(Button, { icon: _jsx(ReloadOutlined, {}), onClick: () => void store.load(), loading: isLoading, children: "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C" })] }), _jsx("div", { "data-testid": "corp-links-table", children: _jsx(Table, { rowKey: "linkId", size: "middle", columns: columns, dataSource: links, loading: isLoading, pagination: links.length > 20 ? { pageSize: 20 } : false, scroll: { x: 1100 }, locale: { emptyText: emptyText ?? "Привязок пока нет" } }) }), _jsx(CompanyLinkEditModal, { store: store, link: editLink, onClose: () => setEditLink(null) }), _jsx(CompanyLinkBlockModal, { store: store, link: blockLink, onClose: () => setBlockLink(null) })] }));
});
CompanyLinks.displayName = "CompanyLinks";
export default CompanyLinks;
